/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
/**
 * SchedulePage — drag-and-drop scheduling with location shifts, color codes,
 * click-to-expand scene with breakdown + remark thread (re-using SceneExpand),
 * and industry-format exports (Call Sheet / Full Schedule / DOOD / Custom).
 * Views: Strip Board (industry standard) | List (timeline) | Board (Kanban) | Calendar | Locations
 * Panels: Fifi AI Co-pilot | Conflict Detection
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ImageLightbox } from '../components/ImageLightbox';
import { AuthImage } from '../components/AuthImage';
import axios from 'axios';
import {
  Plus, Trash2, Edit2, Calendar, Clock, MapPin, Save, X, Download,
  CalendarDays, Zap, ChevronDown, ListChecks, LayoutList, LayoutGrid,
  CheckCircle2, Circle, Search, Scissors, AlertTriangle, RefreshCw,
  Upload, Loader2, Navigation, Wand2, GripVertical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import SceneExpand from '../components/SceneExpand';
import StripBoardView from '../components/schedule/StripBoardView';
import CalendarView from '../components/schedule/CalendarView';
import ConflictPanel, { detectConflicts } from '../components/schedule/ConflictPanel';
import FifiSchedulePanel from '../components/schedule/FifiSchedulePanel';
import { toast } from 'sonner';
import { PdfLanguageDialog } from '../components/callsheet/PdfLanguageDialog';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact add picker for board view — shows a small "Add scene..." dropdown
const BoardAddPicker = ({ day, allScenes, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const daySceneIds = new Set(day.scene_ids || []);
  const available = allScenes.filter(s => !daySceneIds.has(s.id) && (
    !q || String(s.scene_number ?? '').includes(q) || s.location_name?.toLowerCase().includes(q.toLowerCase())
  ));
  if (!allScenes.length) return null;
  return (
    <div className="p-2 border-t border-[var(--cb-border)] relative">
      <button onClick={() => setOpen(o => !o)}
        className="w-full text-[10px] text-[var(--cb-text-dim)] hover:text-[var(--cb-text-sec)] flex items-center gap-1 justify-center py-1 rounded hover:bg-[var(--cb-border)] transition-colors">
        <Plus className="h-2.5 w-2.5" /> Add scene
      </button>
      {open && (
        <div className="absolute bottom-full left-0 right-0 bg-[var(--cb-bg)] border border-[var(--cb-border)] rounded-lg mb-1 shadow-2xl z-20 p-2">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search..." autoFocus
            className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs mb-2" />
          <div className="max-h-40 overflow-y-auto space-y-1">
            {available.slice(0, 15).map(s => (
              <button key={s.id} onClick={() => { onAdd(s.id); setOpen(false); setQ(''); }}
                className="w-full text-left px-2 py-1 rounded hover:bg-[var(--cb-surface-h)] text-xs">
                <span className="font-bold text-[var(--cb-accent)] mr-1.5">{formatSceneNumber(s.scene_number, { prefix: '#' })}</span>
                <span className="text-[var(--cb-text)]">{s.scene_header}</span>
              </button>
            ))}
            {available.length === 0 && <p className="text-[10px] text-[var(--cb-text-dim)] text-center py-2">No unscheduled scenes</p>}
          </div>
        </div>
      )}
    </div>
  );
};

const SHIFT_COLORS = ['#4a9eff', 'var(--cb-accent)', '#44bb88', '#ff6b6b', '#a06bff', '#ff9f43', '#26d0ce', '#ee5a6f'];

const STRIP_COLORS = {
  'INT DAY':   { bg: 'bg-amber-500/20', border: 'border-amber-500/40', text: 'text-amber-300', pill: 'bg-amber-500/30 text-amber-300', accent: '#f59e0b' },
  'INT NIGHT': { bg: 'bg-indigo-500/20', border: 'border-indigo-500/40', text: 'text-indigo-300', pill: 'bg-indigo-500/30 text-indigo-300', accent: '#6366f1' },
  'EXT DAY':   { bg: 'bg-sky-500/20', border: 'border-sky-500/40', text: 'text-sky-300', pill: 'bg-sky-500/30 text-sky-300', accent: '#0ea5e9' },
  'EXT NIGHT': { bg: 'bg-violet-500/20', border: 'border-violet-500/40', text: 'text-violet-300', pill: 'bg-violet-500/30 text-violet-300', accent: '#8b5cf6' },
  'EXT DAWN':  { bg: 'bg-orange-500/20', border: 'border-orange-500/40', text: 'text-orange-300', pill: 'bg-orange-500/30 text-orange-300', accent: '#f97316' },
  'EXT DUSK':  { bg: 'bg-rose-500/20', border: 'border-rose-500/40', text: 'text-rose-300', pill: 'bg-rose-500/30 text-rose-300', accent: '#f43f5e' },
};
const getStripStyle = (scene) => {
  const key = `${(scene.location_type || 'INT').toUpperCase()} ${(scene.time_of_day || 'DAY').toUpperCase()}`;
  return STRIP_COLORS[key] || STRIP_COLORS['INT DAY'];
};

// Compact scene strip for Board columns
const BoardSceneStrip = ({ scene, onRemove, draggable, onDragStart }) => {
  const style = getStripStyle(scene);
  return (
    <div
      className={`flex items-stretch rounded border ${style.bg} ${style.border} mb-1.5 select-none cursor-grab active:cursor-grabbing group/bs`}
      draggable={draggable}
      onDragStart={onDragStart}
      data-testid={`board-strip-${scene.scene_number ?? 'unnumbered'}`}
    >
      <div className="w-1 rounded-l shrink-0" style={{ backgroundColor: style.accent }} />
      <div className="flex-1 p-2 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
          <span className={`text-xs font-bold ${style.text}`}>{formatSceneNumber(scene.scene_number)}</span>
          <span className={`text-[9px] px-1 rounded-full font-medium ${style.pill}`}>{scene.location_type || 'INT'} {scene.time_of_day || 'DAY'}</span>
        </div>
        <p className="text-xs text-[var(--cb-text)] truncate leading-tight">{scene.scene_header}</p>
        <div className="text-[10px] text-[var(--cb-text-dim)] truncate mt-0.5">{scene.location_name} · {(scene.page_count_eighths || 0) / 8}pg</div>
      </div>
      {onRemove && (
        <button onClick={e => { e.stopPropagation(); onRemove(scene.id); }}
          className="px-1 opacity-0 group-hover/bs:opacity-100 transition-opacity text-[var(--cb-text-sec)] hover:text-red-400">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

const SchedulePage = () => {
  const { projectId } = useParams();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [days, setDays] = useState([]);
  const [allScenes, setAllScenes] = useState([]);
  const [breakdownById, setBreakdownById] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [dragSceneId, setDragSceneId] = useState(null);
  const [dragFromDayId, setDragFromDayId] = useState(null);
  const [expandedSceneId, setExpandedSceneId] = useState(null);
  const [editingDayId, setEditingDayId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  // Custom export
  const [customMode, setCustomMode] = useState(false);
  const [customDayIds, setCustomDayIds] = useState([]);
  // Shift dialog
  const [shiftDialogDayId, setShiftDialogDayId] = useState(null);
  const [shiftDraft, setShiftDraft] = useState({ location: '', start_time: '', end_time: '', color: SHIFT_COLORS[0], notes: '' });
  // View mode
  const [viewMode, setViewMode] = useState('strip'); // 'strip' | 'list' | 'board' | 'calendar' | 'locations'
  const [boardDragOver, setBoardDragOver] = useState(null);
  const [boardExpandedSceneId, setBoardExpandedSceneId] = useState(null);
  const [universalSearch, setUniversalSearch] = useState('');
  const [shotCollapsed, setShotCollapsed] = useState({}); // dayId → bool
  const [fromUniversal, setFromUniversal] = useState(false);
  // Universal tray sections
  const [uniSectionShot, setUniSectionShot] = useState(false);
  const [uniSectionDel, setUniSectionDel] = useState(false);
  const [uniSectionSched, setUniSectionSched] = useState(false);
  // Location Specs dialog
  const [showLocSpecs, setShowLocSpecs] = useState(false);
  const [locSpecs, setLocSpecs] = useState([]);
  const [autoScheduleDialog, setAutoScheduleDialog] = useState(false);
  // D&D availability warning
  const [dndWarning, setDndWarning] = useState(null);
  // Locations view
  const [locGallery, setLocGallery] = useState([]);
  const [locGalleryLoading, setLocGalleryLoading] = useState(false);
  const [locSpecsEdits, setLocSpecsEdits] = useState({});
  const [locSaving, setLocSaving] = useState({});
  const [locPhotoUploading, setLocPhotoUploading] = useState({});
  const fileInputRefs = useRef({});
  const [locLightbox, setLocLightbox] = useState(null);
  // ── NEW: Strip board, conflicts, Fifi ──────────────────────────────────────
  const [lockedScenes, setLockedScenes] = useState(new Set()); // scene IDs locked on board
  const [showFifi, setShowFifi] = useState(true);
  const [showConflicts, setShowConflicts] = useState(false);
  const [autoScheduleOptions, setAutoScheduleOptions] = useState({
    pages_per_day: 8,
    prioritize_location_grouping: true,
    shoot_vfx_early: true,
    respect_story_order: false,
    monsoon_mode: false,
  });
  // Strip board drag-over state
  const [stripDragOver, setStripDragOver] = useState(null);
  const [callSheetLanguageDialog, setCallSheetLanguageDialog] = useState({ open: false, dayId: null, dayNum: null });

  // Derived: unique locations + scenes grouped by location
  const uniqueLocations = useMemo(() => {
    const s = new Set(allScenes.map(s => (s.location_name || 'Unknown').trim()).filter(Boolean));
    return Array.from(s).sort();
  }, [allScenes]);

  const scenesByLocation = useMemo(() => {
    const map = {};
    allScenes.forEach(s => {
      const loc = (s.location_name || 'Unknown').trim();
      if (!map[loc]) map[loc] = [];
      map[loc].push(s);
    });
    return map;
  }, [allScenes]);

  const galleryByLocation = useMemo(() => {
    const map = {};
    locGallery.forEach(ref => {
      const loc = (ref.location_name || 'Unknown').trim();
      if (!map[loc]) map[loc] = [];
      map[loc].push(ref);
    });
    return map;
  }, [locGallery]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [schedRes, brkRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/schedule`, { headers }),
        axios.get(`${API}/projects/${projectId}/breakdown`, { headers })
      ]);
      setDays(schedRes.data);
      setAllScenes(brkRes.data);
      const map = {};
      brkRes.data.forEach(s => { map[s.id] = s; });
      setBreakdownById(map);
    } catch { toast.error('Failed to load schedule'); }
    finally { setLoading(false); }
  }, [projectId, headers]);

  const fetchLocSpecs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/location-specs`, { headers });
      const specs = r.data || [];
      setLocSpecs(specs);
      // Sync edits state with loaded specs
      const editsMap = {};
      specs.forEach(s => { editsMap[s.location_name] = { ...s }; });
      setLocSpecsEdits(prev => ({ ...editsMap, ...prev }));
    } catch (err) { console.error('Failed to load location specs:', err); /* non-critical, silent in UI */ }
  }, [projectId, headers]);

  useEffect(() => { fetchAll(); fetchLocSpecs(); }, [fetchAll, fetchLocSpecs]);

  const fetchLocGallery = async () => {
    setLocGalleryLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/gallery?department=ART`, { headers });
      // r.data is grouped [{location_name, rooms: [{items: [...]}]}]
      // Flatten to get all refs
      const flat = [];
      (r.data || []).forEach(group => {
        (group.rooms || []).forEach(room => {
          (room.items || []).forEach(item => flat.push({ ...item, location_name: group.location_name }));
        });
      });
      setLocGallery(flat);
    } catch (err) { console.error('Failed to load location gallery:', err); /* non-critical, silent in UI */ }
    setLocGalleryLoading(false);
  };

  const handleSaveLocationSpec = async (locName) => {
    setLocSaving(p => ({ ...p, [locName]: true }));
    try {
      const editedSpec = locSpecsEdits[locName] || {};
      const newSpecs = locSpecs.filter(s => s.location_name !== locName);
      newSpecs.push({ ...editedSpec, location_name: locName });
      await axios.put(`${API}/projects/${projectId}/location-specs`, newSpecs, { headers });
      setLocSpecs(newSpecs);
      toast.success(`Saved specs for ${locName}`);
    } catch { toast.error('Failed to save'); }
    setLocSaving(p => ({ ...p, [locName]: false }));
  };

  const handleLocPhotoUpload = async (locName, file) => {
    if (!file) return;
    setLocPhotoUploading(p => ({ ...p, [locName]: true }));
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(
        `${API}/projects/${projectId}/gallery?department=ART&location_name=${encodeURIComponent(locName)}&room_name=Reference`,
        form, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Photo uploaded');
      fetchLocGallery();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setLocPhotoUploading(p => ({ ...p, [locName]: false }));
  };

  const handleAutoGenerate = async () => {
    setGenerating(true);
    setAutoScheduleDialog(false);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/schedule/auto`,
        { location_specs: locSpecs, ...autoScheduleOptions },
        { headers }
      );
      const report = res.data.report;
      const scoreMsg = report ? ` · Optimisation score: ${report.optimization_score}%` : '';
      toast.success(`Added ${res.data.total_days_added} day(s)${scoreMsg}`);
      if (report?.warnings?.length > 0) {
        report.warnings.forEach(w => toast.warning(w, { duration: 6000 }));
      }
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Auto-schedule failed'); }
    finally { setGenerating(false); }
  };

  const handleAddDay = async () => {
    const next = (days.length > 0 ? Math.max(...days.map(d => d.day_number)) : 0) + 1;
    try {
      await axios.post(`${API}/projects/${projectId}/schedule`, { day_number: next, scene_ids: [] }, { headers });
      fetchAll();
    } catch { toast.error('Failed to add day'); }
  };

  const [resetting, setResetting] = useState(false);
  const handleResetSchedule = async () => {
    const ok = window.confirm(
      'Reset schedule?\n\nThis clears all shoot days EXCEPT anything in the Finished bin. This action cannot be undone.'
    );
    if (!ok) return;
    setResetting(true);
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/schedule/reset`,
        {},
        { headers }
      );
      toast.success(`Reset complete — cleared ${r.data?.deleted ?? 0} day(s), kept ${r.data?.kept ?? 0}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset schedule');
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteDay = async (dayId) => {
    if (!window.confirm('Delete this shoot day?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/schedule/${dayId}`, { headers });
      toast.success('Day deleted');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handleUpdateDay = async (dayId, updates) => {
    try {
      await axios.patch(`${API}/projects/${projectId}/schedule/${dayId}`, updates, { headers });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  // Location availability check for D&D warnings
  const checkLocationAvailability = (sceneId, toDayId) => {
    if (!locSpecs.length || !toDayId) return null;
    const scene = allScenes.find(s => s.id === sceneId) || breakdownById[sceneId];
    const day = days.find(d => d.id === toDayId);
    if (!scene || !day || !day.shoot_date) return null;
    const locName = (scene.location_name || '').toLowerCase().trim();
    const spec = locSpecs.find(s => s.location_name.toLowerCase().trim() === locName);
    if (!spec || !spec.available_days) return null;
    const dayDate = new Date(day.shoot_date + 'T00:00:00');
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = dayNames[dayDate.getDay()];
    const availDays = spec.available_days.split(',').map(d => d.trim().toLowerCase());
    if (!availDays.includes(dayName.toLowerCase())) {
      return `"${scene.location_name}" is available on ${spec.available_days}. Day ${day.day_number} falls on a ${dayName}${spec.direction ? ` — ${spec.direction}` : ''}.`;
    }
    return null;
  };

  // Drag and drop
  const onDragStart = (e, sceneId, fromDayId, isFromUniversal = false) => {
    setDragSceneId(sceneId);
    setDragFromDayId(fromDayId);
    setFromUniversal(isFromUniversal);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', sceneId);
  };
  const onDrop = async (e, toDayId, toShiftId = null) => {
    e.preventDefault();
    const sceneId = dragSceneId || e.dataTransfer.getData('text/plain');
    if (!sceneId) return;

    // Check location availability warning
    const warning = checkLocationAvailability(sceneId, toDayId);
    if (warning && toDayId) {
      setDndWarning({ sceneId, toDayId, toShiftId, fromDayId: dragFromDayId, isFromUniversal: fromUniversal, message: warning });
      setDragSceneId(null); setDragFromDayId(null); setFromUniversal(false);
      return;
    }

    if (fromUniversal && toDayId) {
      await handleContinueSceneToDay(sceneId, toDayId);
    } else {
      const fromDayId = dragFromDayId;
      // Optimistic update — no visible flash
      const prevDays = days;
      setDays(prev => prev.map(day => {
        if (day.id === fromDayId) return { ...day, scene_ids: (day.scene_ids || []).filter(id => id !== sceneId) };
        if (day.id === toDayId) return { ...day, scene_ids: [...(day.scene_ids || []), sceneId] };
        return day;
      }));
      try {
        await axios.post(`${API}/projects/${projectId}/schedule/reorder`, {
          scene_id: sceneId, from_day_id: fromDayId, to_day_id: toDayId, to_shift_id: toShiftId
        }, { headers });
        // Success: state already up-to-date
      } catch (err) {
        setDays(prevDays); // revert on error
        toast.error(err.response?.data?.detail || 'Reorder failed');
      }
    }
    setDragSceneId(null);
    setDragFromDayId(null);
    setFromUniversal(false);
  };

  const handleDndWarningConfirm = async () => {
    const { sceneId, toDayId, toShiftId, fromDayId, isFromUniversal } = dndWarning;
    setDndWarning(null);
    if (isFromUniversal && toDayId) {
      await handleContinueSceneToDay(sceneId, toDayId);
    } else {
      const prevDays = days;
      setDays(prev => prev.map(day => {
        if (day.id === fromDayId) return { ...day, scene_ids: (day.scene_ids || []).filter(id => id !== sceneId) };
        if (day.id === toDayId) return { ...day, scene_ids: [...(day.scene_ids || []), sceneId] };
        return day;
      }));
      try {
        await axios.post(`${API}/projects/${projectId}/schedule/reorder`, {
          scene_id: sceneId, from_day_id: fromDayId, to_day_id: toDayId, to_shift_id: toShiftId
        }, { headers });
      } catch (err) {
        setDays(prevDays);
        toast.error(err.response?.data?.detail || 'Reorder failed');
      }
    }
  };

  // Add shift to day
  const handleAddShift = async () => {
    if (!shiftDraft.location.trim()) { toast.error('Location required'); return; }
    const day = days.find(d => d.id === shiftDialogDayId);
    if (!day) return;
    const newShift = { ...shiftDraft, id: crypto.randomUUID(), scene_ids: [] };
    const newShifts = [...(day.location_shifts || []), newShift];
    await handleUpdateDay(day.id, { location_shifts: newShifts });
    setShiftDialogDayId(null);
    setShiftDraft({ location: '', start_time: '', end_time: '', color: SHIFT_COLORS[0], notes: '' });
  };

  const handleDeleteShift = async (dayId, shiftId) => {
    const day = days.find(d => d.id === dayId);
    if (!day) return;
    const newShifts = (day.location_shifts || []).filter(s => s.id !== shiftId);
    await handleUpdateDay(dayId, { location_shifts: newShifts });
  };

  // Exports
  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportCallSheet = async (dayId, dayNum) => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/schedule/export/call-sheet/${dayId}`,
        { headers, responseType: 'blob' });
      downloadFile(r.data, `callsheet_day${dayNum}.pdf`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Export failed'); }
  };
  const handleExportCallSheetWithLanguage = async (synopsisLang) => {
    const { dayId, dayNum } = callSheetLanguageDialog;
    if (!dayId) return;
    try {
      const r = await axios.get(
        `${API}/projects/${projectId}/schedule/export/call-sheet/${dayId}?synopsis_lang=${encodeURIComponent(synopsisLang)}`,
        { headers, responseType: 'blob' },
      );
      downloadFile(r.data, `callsheet_day${dayNum}.pdf`);
      setCallSheetLanguageDialog({ open: false, dayId: null, dayNum: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
  };
  const handleExportFull = async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/schedule/export/full-schedule`,
        { headers, responseType: 'blob' });
      downloadFile(r.data, `full_schedule.pdf`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Export failed'); }
  };
  const handleExportDOOD = async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/schedule/export/dood`,
        { headers, responseType: 'blob' });
      downloadFile(r.data, `dood.pdf`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Export failed'); }
  };
  const handleExportCustom = async () => {
    if (customDayIds.length === 0) { toast.error('Pick at least one day'); return; }
    try {
      const params = customDayIds.map(id => `day_ids=${encodeURIComponent(id)}`).join('&');
      const r = await axios.post(`${API}/projects/${projectId}/schedule/export/custom?${params}`, {},
        { headers, responseType: 'blob' });
      downloadFile(r.data, `custom_schedule.pdf`);
      setCustomMode(false);
      setCustomDayIds([]);
    } catch (err) { toast.error(err.response?.data?.detail || 'Export failed'); }
  };

  // Compute unscheduled scenes
  const scheduledIds = new Set(days.flatMap(d => d.scene_ids || []));
  const unscheduled = allScenes.filter(s => !scheduledIds.has(s.id));

  // Real-time conflict detection
  const conflicts = useMemo(() => detectConflicts(days, breakdownById), [days, breakdownById]);

  // Lock/unlock a scene on the strip board
  const handleToggleLock = (sceneId) => {
    setLockedScenes(prev => {
      const next = new Set(prev);
      if (next.has(sceneId)) next.delete(sceneId); else next.add(sceneId);
      return next;
    });
  };

  // Strip board: add scene to day
  const handleStripAddScene = async (sceneId, dayId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/schedule/reorder`, {
        scene_id: sceneId, from_day_id: null, to_day_id: dayId
      }, { headers });
      fetchAll();
    } catch (e) { toast.error('Failed to add scene'); }
  };

  // Board: remove scene from a day
  const handleBoardSceneRemove = async (sceneId, dayId) => {
    const day = days.find(d => d.id === dayId);
    if (!day) return;
    try {
      await axios.patch(`${API}/projects/${projectId}/schedule/${dayId}`,
        { scene_ids: day.scene_ids.filter(id => id !== sceneId) }, { headers });
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  // Mark scene as shot / unshot
  const handleMarkAsShot = async (sceneId, isShot) => {
    try {
      await axios.patch(`${API}/scenes/${sceneId}`, { is_shot: isShot }, { headers });
      setAllScenes(prev => prev.map(s => s.id === sceneId ? { ...s, is_shot: isShot } : s));
      setBreakdownById(prev => ({ ...prev, [sceneId]: { ...prev[sceneId], is_shot: isShot } }));
    } catch { toast.error('Failed to update shot status'); }
  };

  // Mark scene as deleted / undeleted
  const handleMarkAsDeleted = async (sceneId, isDeleted) => {
    try {
      await axios.patch(`${API}/scenes/${sceneId}`, { is_deleted: isDeleted }, { headers });
      setAllScenes(prev => prev.map(s => s.id === sceneId ? { ...s, is_deleted: isDeleted } : s));
      setBreakdownById(prev => ({ ...prev, [sceneId]: { ...prev[sceneId], is_deleted: isDeleted } }));
      toast.success(isDeleted ? 'Scene marked as deleted' : 'Scene restored');
    } catch { toast.error('Failed'); }
  };

  // Add scene to a day (from universal tray) — "Continue" mode (no removal from other days)
  const handleContinueSceneToDay = async (sceneId, toDayId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/schedule/reorder`, {
        scene_id: sceneId,
        from_day_id: null,
        to_day_id: toDayId,
      }, { headers });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="min-h-screen bg-[var(--cb-bg)]" data-testid="schedule-page">
      <div className="p-5 border-b border-[var(--cb-border)] bg-[var(--cb-bg)] sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <CalendarDays className="h-6 w-6 text-[var(--cb-accent)]" />
            <h1 className="font-['DM_Serif_Display'] text-2xl md:text-3xl text-[var(--cb-text)]">Schedule</h1>
            <Badge variant="outline" className="border-[var(--cb-border)] text-[var(--cb-text-sec)] text-xs">{days.length} days · {scheduledIds.size}/{allScenes.length} scheduled</Badge>
            {conflicts.length > 0 && (
              <button
                onClick={() => setShowConflicts(c => !c)}
                className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs hover:bg-red-500/20 transition-colors"
                data-testid="conflicts-indicator"
              >
                <AlertTriangle className="h-3 w-3" />
                {conflicts.length} conflict{conflicts.length > 1 ? 's' : ''}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* View toggle */}
            <div className="flex bg-[var(--cb-surface-h)] border border-[var(--cb-border)] rounded-lg p-0.5">
              <Button size="sm" variant="ghost" onClick={() => setViewMode('strip')}
                className={`h-7 px-2.5 text-xs ${viewMode === 'strip' ? 'bg-[var(--cb-accent)] text-black' : 'text-[var(--cb-text-sec)]'}`}
                data-testid="schedule-strip-view-btn">
                <GripVertical className="h-3.5 w-3.5 mr-1" /> Strip
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setViewMode('calendar')}
                className={`h-7 px-2.5 text-xs ${viewMode === 'calendar' ? 'bg-[#4a9eff] text-white' : 'text-[var(--cb-text-sec)]'}`}
                data-testid="schedule-calendar-view-btn">
                <Calendar className="h-3.5 w-3.5 mr-1" /> Cal
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setViewMode('list')}
                className={`h-7 px-2.5 text-xs ${viewMode === 'list' ? 'bg-[#44bb88] text-black' : 'text-[var(--cb-text-sec)]'}`}
                data-testid="schedule-list-view-btn">
                <LayoutList className="h-3.5 w-3.5 mr-1" /> List
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setViewMode('board')}
                className={`h-7 px-2.5 text-xs ${viewMode === 'board' ? 'bg-[#a06bff] text-white' : 'text-[var(--cb-text-sec)]'}`}
                data-testid="schedule-board-view-btn">
                <LayoutGrid className="h-3.5 w-3.5 mr-1" /> Board
              </Button>
              <Button size="sm" variant="ghost"
                onClick={() => { setViewMode('locations'); if (!locGallery.length) fetchLocGallery(); }}
                className={`h-7 px-2.5 text-xs ${viewMode === 'locations' ? 'bg-[#4ade80] text-black' : 'text-[var(--cb-text-sec)]'}`}
                data-testid="schedule-locations-view-btn">
                <MapPin className="h-3.5 w-3.5 mr-1" /> Locs
              </Button>
            </div>
            <Button size="sm" variant="outline" onClick={handleAddDay} className="border-[var(--cb-border)] text-[var(--cb-text)] hover:bg-[var(--cb-surface-h)] h-8" data-testid="add-day-btn">
              <Plus className="h-3 w-3 mr-1" /> Add Day
            </Button>
            <Button size="sm" variant="outline" onClick={handleResetSchedule} disabled={resetting}
              className="border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white h-8"
              data-testid="reset-schedule-btn"
              title="Clear all shoot days (keeps anything in the Finished bin)">
              <X className="h-3 w-3 mr-1" /> {resetting ? 'Resetting…' : 'Reset'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleExportFull} className="border-[var(--cb-accent)]/40 text-[var(--cb-accent)] hover:bg-[var(--cb-accent)] hover:text-black h-8" data-testid="export-full-btn">
              <Download className="h-3 w-3 mr-1" /> Full
            </Button>
            <Button size="sm" variant="outline" onClick={handleExportDOOD} className="border-[#4a9eff]/40 text-[#4a9eff] hover:bg-[#4a9eff] hover:text-black h-8" data-testid="export-dood-btn">
              <Download className="h-3 w-3 mr-1" /> DOOD
            </Button>
            <Button size="sm" variant={customMode ? 'default' : 'outline'} onClick={() => { setCustomMode(!customMode); setCustomDayIds([]); }} className={customMode ? 'bg-[var(--cb-accent)] text-black h-8' : 'border-[var(--cb-border)] text-[var(--cb-text-sec)] h-8'} data-testid="custom-export-toggle">
              <ListChecks className="h-3 w-3 mr-1" /> Custom
            </Button>
            {customMode && (
              <Button size="sm" onClick={handleExportCustom} disabled={customDayIds.length === 0} className="bg-[#4a9eff] hover:bg-[#3b8fee] text-white h-8" data-testid="export-custom-btn">
                Export {customDayIds.length} day(s)
              </Button>
            )}
            {/* Fifi AI toggle */}
            <Button
              size="sm"
              variant={showFifi ? 'default' : 'outline'}
              onClick={() => { setShowFifi(f => !f); setShowConflicts(false); }}
              className={showFifi ? 'bg-[var(--cb-accent)] text-black h-8' : 'border-[var(--cb-border)] text-[var(--cb-text-sec)] h-8'}
              data-testid="fifi-schedule-toggle"
            >
              <Wand2 className="h-3 w-3 mr-1" /> Fifi AI
            </Button>
            {/* Conflicts panel toggle */}
            <Button
              size="sm"
              variant={showConflicts ? 'default' : 'outline'}
              onClick={() => { setShowConflicts(c => !c); setShowFifi(false); }}
              className={showConflicts
                ? 'bg-red-600 text-white h-8'
                : `border-[var(--cb-border)] h-8 ${conflicts.length > 0 ? 'text-red-400 border-red-500/30' : 'text-[var(--cb-text-sec)]'}`}
              data-testid="conflicts-panel-toggle"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              Conflicts {conflicts.length > 0 && `(${conflicts.length})`}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Main layout: content + optional right panel ────────────────────── */}
      <div className="flex h-[calc(100vh-73px)] overflow-hidden">
        {/* Content area */}
        <div className={`flex-1 overflow-auto ${viewMode === 'strip' ? 'p-4' : viewMode === 'board' ? '' : 'p-5'}`}>
        {loading ? (
          <div className="text-center py-16 text-[var(--cb-text-sec)] w-full">Loading schedule…</div>
        ) : viewMode === 'strip' ? (
          /* ── INDUSTRY STRIP BOARD ── */
          <StripBoardView
            days={days}
            allScenes={allScenes}
            breakdownById={breakdownById}
            onRemoveScene={(sceneId, dayId) => handleBoardSceneRemove(sceneId, dayId)}
            onDragStart={(e, sceneId, dayId) => onDragStart(e, sceneId, dayId)}
            onDragOver={(dayId) => setStripDragOver(dayId)}
            onDrop={(e, dayId) => onDrop(e, dayId)}
            onAddScene={handleStripAddScene}
            conflicts={conflicts}
            lockedScenes={lockedScenes}
            onToggleLock={handleToggleLock}
            projectId={projectId}
            token={token}
          />
        ) : viewMode === 'calendar' ? (
          /* ── CALENDAR VIEW ── */
          <CalendarView days={days} allScenes={allScenes} breakdownById={breakdownById} />
        ) : viewMode === 'locations' ? (
          /* ── LOCATIONS VIEW ── */
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-['DM_Serif_Display'] text-xl text-[var(--cb-text)]">Locations</h2>
                <p className="text-xs text-[var(--cb-text-sec)] mt-0.5">{uniqueLocations.length} unique locations from your script. Set availability, directions, and manage reference photos.</p>
              </div>
            </div>
            {uniqueLocations.length === 0 ? (
              <div className="text-center py-12 text-[var(--cb-text-sec)]">
                <MapPin className="h-12 w-12 mx-auto mb-3 text-[var(--cb-border)]" />
                <p>No locations found. Upload a script to populate locations.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {uniqueLocations.map(locName => {
                  const locScenes = scenesByLocation[locName] || [];
                  const photos = galleryByLocation[locName] || [];
                  const spec = locSpecsEdits[locName] || locSpecs.find(s => s.location_name === locName) || {};
                  const isSav = locSaving[locName] || false;
                  const isUpload = locPhotoUploading[locName] || false;

                  return (
                    <div
                      key={locName}
                      className="bg-[var(--cb-surface-h)] border border-[var(--cb-border)] rounded-xl overflow-hidden"
                      data-testid={`loc-card-${locName.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {/* Location header */}
                      <div className="bg-[var(--cb-bg)] px-4 py-3 flex items-center justify-between border-b border-[var(--cb-border)]">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-[#4ade80]" />
                          <span className="font-semibold text-[var(--cb-text)]">{locName}</span>
                          <span className="text-xs text-[var(--cb-text-sec)]">{locScenes.length} scene{locScenes.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex gap-1">
                          {locScenes.slice(0, 5).map(s => (
                            <span key={s.id} className="text-[9px] bg-[var(--cb-accent)]/20 text-[var(--cb-accent)] px-1 rounded font-mono">{formatSceneNumber(s.scene_number, { prefix: '#' })}</span>
                          ))}
                          {locScenes.length > 5 && <span className="text-[9px] text-[var(--cb-text-dim)]">+{locScenes.length - 5}</span>}
                        </div>
                      </div>

                      {/* Photo strip */}
                      <div className="p-3 border-b border-[var(--cb-border)]">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] font-semibold text-[var(--cb-text-sec)] uppercase tracking-widest">Location Photos</span>
                          {locGalleryLoading && <Loader2 className="h-3 w-3 animate-spin text-[var(--cb-text-sec)]" />}
                        </div>
                        <div className="flex items-center gap-2 overflow-x-auto pb-1">
                          {photos.length === 0 && !locGalleryLoading && (
                            <span className="text-xs text-[var(--cb-border)] italic shrink-0">No photos yet</span>
                          )}
                          {photos.map((photo, pIdx) => (
                            <AuthImage
                              key={photo.id}
                              path={photo.storage_path}
                              alt={photo.original_filename}
                              className="h-20 w-28 object-cover rounded-lg shrink-0 border border-[var(--cb-border)] hover:border-[#4ade80] transition-colors cursor-pointer hover:scale-105"
                              onClick={() => setLocLightbox({
                                images: photos.map(p => ({ path: p.storage_path, alt: p.original_filename })),
                                idx: pIdx
                              })}
                              data-testid={`loc-photo-${photo.id}`}
                            />
                          ))}
                          {/* Upload button */}
                          <button
                            className="h-20 w-20 border-2 border-dashed border-[var(--cb-border)] hover:border-[#4ade80] rounded-lg flex flex-col items-center justify-center gap-1 text-[var(--cb-text-dim)] hover:text-[#4ade80] transition-colors shrink-0"
                            onClick={() => fileInputRefs.current[locName]?.click()}
                            disabled={isUpload}
                            data-testid={`upload-loc-photo-btn-${locName}`}
                          >
                            {isUpload ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                            <span className="text-[9px]">Add Photo</span>
                          </button>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            ref={el => { fileInputRefs.current[locName] = el; }}
                            onChange={e => { if (e.target.files?.[0]) handleLocPhotoUpload(locName, e.target.files[0]); e.target.value = ''; }}
                          />
                        </div>
                      </div>

                      {/* Spec fields */}
                      <div className="p-4 space-y-3">
                        <div className="text-[10px] font-semibold text-[var(--cb-text-sec)] uppercase tracking-widest">Location Specifications</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="flex items-center gap-1 text-[10px] text-[var(--cb-text-sec)] mb-1">
                              <Calendar className="h-3 w-3" /> Available Days
                            </label>
                            <Input
                              value={spec.available_days || ''}
                              onChange={e => setLocSpecsEdits(p => ({ ...p, [locName]: { ...spec, available_days: e.target.value } }))}
                              placeholder="Mon,Tue,Wed"
                              className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs"
                              data-testid={`loc-spec-avail-days-${locName}`}
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-1 text-[10px] text-[var(--cb-text-sec)] mb-1">
                              <Clock className="h-3 w-3" /> Shooting Hours
                            </label>
                            <Input
                              value={spec.available_hours || ''}
                              onChange={e => setLocSpecsEdits(p => ({ ...p, [locName]: { ...spec, available_hours: e.target.value } }))}
                              placeholder="06:00-20:00"
                              className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs"
                              data-testid={`loc-spec-hours-${locName}`}
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-1 text-[10px] text-[var(--cb-text-sec)] mb-1">
                              <MapPin className="h-3 w-3" /> Distance (km)
                            </label>
                            <Input
                              type="number"
                              value={spec.distance_km || ''}
                              onChange={e => setLocSpecsEdits(p => ({ ...p, [locName]: { ...spec, distance_km: parseFloat(e.target.value) || null } }))}
                              placeholder="e.g. 12"
                              className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs"
                              data-testid={`loc-spec-km-${locName}`}
                            />
                          </div>
                          <div>
                            <label className="flex items-center gap-1 text-[10px] text-[var(--cb-text-sec)] mb-1">
                              <Navigation className="h-3 w-3" /> Direction from Base
                            </label>
                            <Input
                              value={spec.direction || ''}
                              onChange={e => setLocSpecsEdits(p => ({ ...p, [locName]: { ...spec, direction: e.target.value } }))}
                              placeholder="e.g. 10 kms via NH47 north"
                              className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs"
                              data-testid={`loc-spec-direction-card-${locName}`}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="flex items-center gap-1 text-[10px] text-[var(--cb-text-sec)] mb-1">
                            Notes
                          </label>
                          <Input
                            value={spec.notes || ''}
                            onChange={e => setLocSpecsEdits(p => ({ ...p, [locName]: { ...spec, notes: e.target.value } }))}
                            placeholder="Permit required, noise restrictions, etc."
                            className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs"
                            data-testid={`loc-spec-notes-${locName}`}
                          />
                        </div>
                        <div className="flex justify-end">
                          <Button
                            size="sm"
                            onClick={() => handleSaveLocationSpec(locName)}
                            disabled={isSav}
                            className="bg-[#4ade80] hover:bg-[#3bcf6f] text-black h-7 text-xs"
                            data-testid={`save-loc-spec-btn-${locName}`}
                          >
                            {isSav ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : days.length === 0 ? (
          <div className="text-center py-16 w-full">
            <CalendarDays className="h-16 w-16 text-[var(--cb-border)] mx-auto mb-4" />
            <p className="text-[var(--cb-text-sec)] mb-4">No shoot days yet</p>
            <Button onClick={handleAutoGenerate} disabled={generating} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black">
              <Zap className="h-4 w-4 mr-1" /> Auto-Schedule from Scenes
            </Button>
          </div>

        ) : viewMode === 'board' ? (
          /* ── BOARD VIEW (Kanban columns + Universal scenes tray) ── */
          <>
            {/* Universal scenes tray (left) */}
            <div
              className="w-52 shrink-0 border-r border-[var(--cb-border)] bg-[var(--cb-bg)] flex flex-col overflow-hidden"
              data-testid="universal-scenes-tray"
            >
              <div className="p-2 border-b border-[var(--cb-border)]">
                <div className="text-[10px] font-semibold text-[var(--cb-text-sec)] uppercase tracking-widest mb-2">
                  Scene Pool
                </div>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-[var(--cb-text-dim)]" />
                  <input
                    value={universalSearch}
                    onChange={e => setUniversalSearch(e.target.value)}
                    placeholder="Search scenes..."
                    className="w-full bg-[var(--cb-surface-h)] border border-[var(--cb-border)] rounded-md text-[10px] text-[var(--cb-text)] pl-6 pr-2 py-1.5 placeholder-[var(--cb-border)] focus:outline-none focus:border-[var(--cb-accent)]/50"
                    data-testid="universal-scene-search"
                  />
                </div>
              </div>

              {/* Active (unscheduled, non-shot, non-deleted) scenes */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1 min-h-0">
                {(() => {
                  const active = allScenes.filter(s =>
                    !s.is_shot && !s.is_deleted &&
                    !days.some(d => (d.scene_ids || []).includes(s.id)) &&
                    (!universalSearch ||
                      String(s.scene_number ?? '').includes(universalSearch) ||
                      s.scene_header?.toLowerCase().includes(universalSearch.toLowerCase()) ||
                      s.location_name?.toLowerCase().includes(universalSearch.toLowerCase()))
                  );
                  if (!active.length) return (
                    <div className="text-center py-4 text-[10px] text-[var(--cb-border)]">
                      {universalSearch ? 'No matches' : 'All scenes scheduled'}
                    </div>
                  );
                  return active.map(s => {
                    const style = getStripStyle(s);
                    return (
                      <div key={s.id} draggable
                        onDragStart={e => { e.dataTransfer.setData('text/plain', s.id); onDragStart(e, s.id, null, true); }}
                        className={`flex items-stretch rounded border cursor-grab active:cursor-grabbing select-none group/us ${style.bg} ${style.border} hover:brightness-110 transition-all`}
                        data-testid={`universal-scene-${s.scene_number}`}
                      >
                        <div className="w-0.5 rounded-l shrink-0" style={{ backgroundColor: style.accent }} />
                        <div className="flex-1 p-1.5 min-w-0">
                          <div className="flex items-center gap-1">
                            <span className={`text-[10px] font-bold ${style.text}`}>{formatSceneNumber(s.scene_number, { prefix: '#' })}</span>
                            <span className={`text-[8px] px-1 rounded-full ${style.pill}`}>{s.location_type} {s.time_of_day}</span>
                          </div>
                          <p className="text-[9px] text-[var(--cb-text-sec)] truncate mt-0.5">{s.scene_header}</p>
                        </div>
                        <div className="flex flex-col gap-0.5 px-0.5 opacity-0 group-hover/us:opacity-100 transition-opacity justify-center">
                          <button onClick={e => { e.stopPropagation(); handleMarkAsShot(s.id, true); }}
                            title="Mark as shot" className="text-[var(--cb-text-dim)] hover:text-green-400"><CheckCircle2 className="h-2.5 w-2.5" /></button>
                          <button onClick={e => { e.stopPropagation(); handleMarkAsDeleted(s.id, true); }}
                            title="Mark as deleted" className="text-[var(--cb-text-dim)] hover:text-red-400"><Scissors className="h-2.5 w-2.5" /></button>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Scheduled section */}
              {(() => {
                const sched = allScenes.filter(s => !s.is_shot && !s.is_deleted && days.some(d => (d.scene_ids || []).includes(s.id)));
                if (!sched.length) return null;
                return (
                  <div className="border-t border-[var(--cb-border)] shrink-0">
                    <button onClick={() => setUniSectionSched(v => !v)}
                      className="w-full flex items-center justify-between px-2.5 py-1.5 text-[10px] text-[var(--cb-text-dim)] hover:text-[var(--cb-text-sec)] transition-colors"
                      data-testid="uni-sched-toggle">
                      <span>Scheduled ({sched.length})</span>
                      <ChevronDown className={`h-3 w-3 transition-transform ${uniSectionSched ? 'rotate-180' : ''}`} />
                    </button>
                    {uniSectionSched && (
                      <div className="pb-2 px-2 space-y-1 max-h-40 overflow-y-auto">
                        {sched.map(s => {
                          const inDays = days.filter(d => (d.scene_ids || []).includes(s.id));
                          return (
                            <div key={s.id} className="flex items-center gap-1 rounded border border-[var(--cb-border)] bg-[var(--cb-bg)] px-1.5 py-1 group/ss">
                              <span className="text-[9px] text-[var(--cb-accent)] font-bold shrink-0">{formatSceneNumber(s.scene_number, { prefix: '#' })}</span>
                              <span className="text-[9px] text-[var(--cb-text-sec)] flex-1 truncate">{s.scene_header}</span>
                              {inDays.map(d => <span key={d.id} className="text-[8px] px-0.5 rounded bg-green-500/20 text-green-400 shrink-0">D{d.day_number}</span>)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Shot section */}
              {(() => {
                const shot = allScenes.filter(s => s.is_shot);
                if (!shot.length) return null;
                return (
                  <div className="border-t border-[var(--cb-border)] shrink-0">
                    <button onClick={() => setUniSectionShot(v => !v)}
                      className="w-full flex items-center justify-between px-2.5 py-1.5 text-[10px] text-green-600 hover:text-green-400 transition-colors"
                      data-testid="uni-shot-toggle">
                      <span>Shot ({shot.length})</span>
                      <ChevronDown className={`h-3 w-3 transition-transform ${uniSectionShot ? 'rotate-180' : ''}`} />
                    </button>
                    {uniSectionShot && (
                      <div className="pb-2 px-2 space-y-1 max-h-36 overflow-y-auto">
                        {shot.map(s => (
                          <div key={s.id} className="flex items-center gap-1 rounded border border-[var(--cb-border)] bg-[var(--cb-bg)] px-1.5 py-1 group/sh">
                            <span className="text-[9px] line-through text-[var(--cb-text-dim)] flex-1 truncate">{formatSceneNumber(s.scene_number, { prefix: '#' })} {s.scene_header}</span>
                            <button onClick={() => handleMarkAsShot(s.id, false)} title="Unmark shot"
                              className="opacity-0 group-hover/sh:opacity-100 transition-opacity text-[var(--cb-text-dim)] hover:text-amber-400 shrink-0"
                              data-testid={`unmark-shot-uni-${s.scene_number}`}>
                              <RefreshCw className="h-2.5 w-2.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Deleted section */}
              {(() => {
                const deleted = allScenes.filter(s => s.is_deleted);
                if (!deleted.length) return null;
                return (
                  <div className="border-t border-[var(--cb-border)] shrink-0">
                    <button onClick={() => setUniSectionDel(v => !v)}
                      className="w-full flex items-center justify-between px-2.5 py-1.5 text-[10px] text-red-600 hover:text-red-400 transition-colors"
                      data-testid="uni-deleted-toggle">
                      <span>Deleted ({deleted.length})</span>
                      <ChevronDown className={`h-3 w-3 transition-transform ${uniSectionDel ? 'rotate-180' : ''}`} />
                    </button>
                    {uniSectionDel && (
                      <div className="pb-2 px-2 space-y-1 max-h-36 overflow-y-auto">
                        {deleted.map(s => (
                          <div key={s.id} className="flex items-center gap-1 rounded border border-red-500/20 bg-[var(--cb-bg)] px-1.5 py-1 group/del">
                            <span className="text-[9px] line-through text-red-800 flex-1 truncate">{formatSceneNumber(s.scene_number, { prefix: '#' })} {s.scene_header}</span>
                            <button onClick={() => handleMarkAsDeleted(s.id, false)} title="Restore scene"
                              className="opacity-0 group-hover/del:opacity-100 transition-opacity text-[var(--cb-text-dim)] hover:text-amber-400 shrink-0"
                              data-testid={`restore-del-uni-${s.scene_number}`}>
                              <RefreshCw className="h-2.5 w-2.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Day columns (horizontal scroll, 4 visible at a time) */}
            <div className="flex-1 overflow-x-auto overflow-y-hidden p-3" data-testid="board-view">
              <div className="flex gap-3 h-full" style={{ minWidth: Math.max(days.length * 244, 800) + 'px' }}>
                {days.map(day => {
                  const isDragOver = boardDragOver === day.id;
                  const allDayScenes = (day.scenes || []).map(s => breakdownById[s.id] || s);
                  const activeScenes = allDayScenes.filter(s => !s.is_shot);
                  const shotScenes = allDayScenes.filter(s => s.is_shot);
                  const isShotCollapsed = shotCollapsed[day.id] !== false; // default collapsed

                  // Determine CONT scenes in this day (same scene in 2+ days)
                  const continuedIds = new Set(
                    allDayScenes.filter(s => days.filter(d => (d.scene_ids||[]).includes(s.id)).length > 1).map(s => s.id)
                  );

                  return (
                    <div
                      key={day.id}
                      className={`flex flex-col bg-[var(--cb-surface-h)] border rounded-xl shrink-0 transition-colors ${isDragOver ? 'border-[var(--cb-accent)] shadow-lg shadow-[var(--cb-accent)]/10' : 'border-[var(--cb-border)]'}`}
                      style={{ width: 240, minWidth: 240, maxWidth: 240 }}
                      onDragOver={e => { e.preventDefault(); setBoardDragOver(day.id); }}
                      onDragLeave={() => setBoardDragOver(null)}
                      onDrop={e => { e.preventDefault(); setBoardDragOver(null); onDrop(e, day.id); }}
                      data-testid={`board-day-${day.day_number}`}
                    >
                      {/* Column header */}
                      <div className="p-2.5 border-b border-[var(--cb-border)] shrink-0">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-1.5">
                            <div className="h-7 w-7 rounded-lg flex items-center justify-center font-bold text-xs shrink-0"
                              style={{ backgroundColor: `${day.day_color || 'var(--cb-accent)'}33`, color: day.day_color || 'var(--cb-accent)' }}>
                              {day.day_number}
                            </div>
                            <span className="text-sm font-semibold text-[var(--cb-text)]">Day {day.day_number}</span>
                            {day.is_day_end && <Badge className="bg-red-500/20 text-red-400 border-0 text-[9px] px-1">WRAP</Badge>}
                          </div>
                          <div className="flex gap-0.5">
                            <Button size="sm" variant="ghost" onClick={() => { setEditingDayId(day.id); setEditDraft({ shoot_date: day.shoot_date, call_time: day.call_time, wrap_time: day.wrap_time, location: day.location, day_color: day.day_color, is_day_end: day.is_day_end }); }}
                              className="h-5 w-5 p-0 text-[var(--cb-text-sec)] hover:text-[var(--cb-accent)]"><Edit2 className="h-3 w-3" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => setShiftDialogDayId(day.id)}
                              className="h-5 w-5 p-0 text-[var(--cb-text-sec)] hover:text-[#4a9eff]"><Plus className="h-3 w-3" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => setCallSheetLanguageDialog({ open: true, dayId: day.id, dayNum: day.day_number })}
                              className="h-5 w-5 p-0 text-[var(--cb-text-sec)] hover:text-[var(--cb-accent)]" data-testid={`board-export-callsheet-${day.day_number}`}><Download className="h-3 w-3" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteDay(day.id)}
                              className="h-5 w-5 p-0 text-[var(--cb-text-sec)] hover:text-red-400"><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </div>
                        {day.shoot_date && <div className="flex items-center gap-1 text-[10px] text-[var(--cb-text-sec)] mb-0.5"><Calendar className="h-2.5 w-2.5" />{day.shoot_date}</div>}
                        {day.call_time && <div className="flex items-center gap-1 text-[10px] text-[var(--cb-text-sec)] mb-0.5"><Clock className="h-2.5 w-2.5" />Call {day.call_time}{day.wrap_time ? `–${day.wrap_time}` : ''}</div>}
                        {day.location && <div className="flex items-center gap-1 text-[10px] text-[var(--cb-text-sec)] mb-0.5"><MapPin className="h-2.5 w-2.5" /><span className="truncate">{day.location}</span></div>}
                        {(day.location_shifts || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {day.location_shifts.map(sh => (
                              <div key={sh.id} className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px]"
                                style={{ borderWidth: 1, borderColor: `${sh.color}88`, backgroundColor: `${sh.color}15` }}>
                                <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: sh.color }} />
                                <span className="text-[var(--cb-text)]">{sh.location}</span>
                                <button onClick={() => handleDeleteShift(day.id, sh.id)} className="text-[var(--cb-text-sec)] hover:text-red-400 ml-0.5"><X className="h-2 w-2" /></button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex justify-between mt-1.5 text-[10px] text-[var(--cb-text-dim)]">
                          <span>{activeScenes.length} active · {shotScenes.length} shot</span>
                          <span className="text-[var(--cb-accent)]">{allDayScenes.reduce((s, sc) => s + (sc.page_count_eighths || 0), 0) / 8}pg</span>
                        </div>
                      </div>

                      {/* Active scenes */}
                      <div className="flex-1 overflow-y-auto p-2 min-h-[80px]">
                        {activeScenes.length === 0 && (
                          <div className="h-16 flex items-center justify-center text-[10px] text-[var(--cb-border)] border-2 border-dashed border-[var(--cb-border)] rounded-lg">
                            Drop scenes here
                          </div>
                        )}
                        {activeScenes.map(sc => {
                          const style = getStripStyle(sc);
                          const isExpanded = boardExpandedSceneId === sc.id;
                          const isCont = continuedIds.has(sc.id);
                          return (
                            <div key={sc.id} className="mb-1.5">
                              <div
                                className={`flex items-stretch rounded border cursor-pointer group/bs select-none transition-all
                                  ${isExpanded ? 'border-[var(--cb-accent)] shadow-md shadow-[var(--cb-accent)]/10' : `${style.bg} ${style.border} hover:brightness-110`}
                                `}
                                draggable
                                onDragStart={e => { e.dataTransfer.setData('text/plain', sc.id); onDragStart(e, sc.id, day.id); }}
                                onClick={() => setBoardExpandedSceneId(isExpanded ? null : sc.id)}
                                data-testid={`board-strip-${sc.scene_number ?? 'unnumbered'}`}
                              >
                                <div className="w-1 rounded-l shrink-0" style={{ backgroundColor: style.accent }} />
                                <div className="flex-1 p-1.5 min-w-0">
                                  <div className="flex items-center gap-1 mb-0.5 flex-wrap">
                                    <span className={`text-[10px] font-bold ${style.text}`}>{formatSceneNumber(sc.scene_number, { prefix: '#' })}</span>
                                    <span className={`text-[8px] px-1 rounded-full ${style.pill}`}>{sc.location_type} {sc.time_of_day}</span>
                                    {isCont && <span className="text-[8px] px-1 rounded bg-amber-500/20 text-amber-400 font-medium">CONT</span>}
                                  </div>
                                  <p className="text-[10px] text-[var(--cb-text)] truncate">{sc.scene_header}</p>
                                  <p className="text-[9px] text-[var(--cb-text-dim)] truncate">{sc.location_name}</p>
                                </div>
                                {/* Shot toggle + Delete toggle */}
                                <button
                                  onClick={e => { e.stopPropagation(); handleMarkAsShot(sc.id, !sc.is_shot); }}
                                  className={`px-1 opacity-0 group-hover/bs:opacity-100 transition-opacity shrink-0 ${sc.is_shot ? 'text-green-400' : 'text-[var(--cb-text-sec)] hover:text-green-400'}`}
                                  title={sc.is_shot ? 'Unmark as shot' : 'Mark as shot'}
                                  data-testid={`mark-shot-${sc.scene_number ?? 'unnumbered'}`}
                                >
                                  {sc.is_shot ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                                </button>
                                <button
                                  onClick={e => { e.stopPropagation(); handleMarkAsDeleted(sc.id, true); }}
                                  className="px-1 opacity-0 group-hover/bs:opacity-100 transition-opacity text-[var(--cb-text-sec)] hover:text-red-400 shrink-0"
                                  title="Mark as deleted"
                                  data-testid={`mark-del-${sc.scene_number ?? 'unnumbered'}`}
                                >
                                  <Scissors className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={e => { e.stopPropagation(); handleBoardSceneRemove(sc.id, day.id); }}
                                  className="px-1 opacity-0 group-hover/bs:opacity-100 transition-opacity text-[var(--cb-text-sec)] hover:text-red-400 shrink-0"
                                  data-testid={`remove-strip-${sc.scene_number ?? 'unnumbered'}`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                              {/* SceneExpand as Dialog overlay - not inline */}
                            </div>
                          );
                        })}
                      </div>

                      {/* Shot scenes — collapsible at bottom */}
                      {shotScenes.length > 0 && (
                        <div className="border-t border-[var(--cb-border)] shrink-0">
                          <button
                            onClick={() => setShotCollapsed(prev => ({ ...prev, [day.id]: !isShotCollapsed }))}
                            className="w-full flex items-center justify-between px-2.5 py-1.5 text-[10px] text-[var(--cb-text-dim)] hover:text-[var(--cb-text-sec)] transition-colors"
                            data-testid={`shot-collapse-${day.day_number}`}
                          >
                            <span>Shot ({shotScenes.length})</span>
                            <ChevronDown className={`h-3 w-3 transition-transform ${isShotCollapsed ? '' : 'rotate-180'}`} />
                          </button>
                          {!isShotCollapsed && (
                            <div className="px-2 pb-2 space-y-1">
                              {shotScenes.map(sc => (
                                <div key={sc.id}
                                  className="flex items-center gap-1 rounded border border-[var(--cb-border)] bg-[var(--cb-bg)] px-1.5 py-1 group/shot"
                                  data-testid={`shot-scene-${sc.scene_number ?? 'unnumbered'}`}
                                >
                                  <span className="text-[9px] line-through text-[var(--cb-text-dim)] flex-1 truncate">{formatSceneNumber(sc.scene_number, { prefix: '#' })} {sc.scene_header}</span>
                                  <button
                                    onClick={() => handleMarkAsShot(sc.id, false)}
                                    className="opacity-0 group-hover/shot:opacity-100 transition-opacity text-[var(--cb-text-dim)] hover:text-amber-400"
                                    title="Unmark as shot"
                                    data-testid={`unmark-shot-${sc.scene_number ?? 'unnumbered'}`}
                                  >
                                    <Circle className="h-2.5 w-2.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Add from picker */}
                      <BoardAddPicker day={day} allScenes={allScenes} onAdd={(sceneId) => handleContinueSceneToDay(sceneId, day.id)} />
                    </div>
                  );
                })}
              </div>
            </div>
          </>

        ) : (
          /* ── LIST VIEW (existing timeline layout) ── */
          <div className="space-y-3" data-testid="schedule-days-list">
            {/* Unscheduled tray */}
            {unscheduled.length > 0 && (
              <Card
                className="bg-[var(--cb-surface-h)] border-2 border-dashed border-[var(--cb-border)] p-4"
                onDragOver={e => e.preventDefault()}
                onDrop={e => onDrop(e, null)}
                data-testid="unscheduled-tray"
              >
                <div className="text-xs uppercase tracking-wider text-[var(--cb-text-sec)] mb-2">Unscheduled ({unscheduled.length})</div>
                <div className="flex flex-wrap gap-2">
                  {unscheduled.map(s => (
                    <div
                      key={s.id}
                      draggable
                      onDragStart={e => onDragStart(e, s.id, null)}
                      className="bg-[var(--cb-bg)] border border-[var(--cb-border)] rounded px-3 py-1.5 cursor-grab hover:border-[var(--cb-accent)] transition-colors text-xs"
                      data-testid={`unscheduled-scene-${s.scene_number}`}
                    >
                      <span className="font-bold text-[var(--cb-accent)]">{formatSceneNumber(s.scene_number, { prefix: '#' })}</span>
                      <span className="text-[var(--cb-text-sec)] ml-2">{s.location_name}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Days */}
            {days.map(day => (
              <Card
                key={day.id}
                className="bg-[var(--cb-surface-h)] border overflow-hidden"
                style={{ borderLeftWidth: '4px', borderLeftColor: day.day_color || 'var(--cb-accent)', borderColor: day.is_day_end ? '#ff6b6b' : 'var(--cb-border)' }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => onDrop(e, day.id)}
                data-testid={`schedule-day-${day.day_number}`}
              >
                {/* Day header */}
                <div className="px-4 py-3 bg-[var(--cb-bg)]/40 border-b border-[var(--cb-border)] flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    {customMode && (
                      <input
                        type="checkbox"
                        checked={customDayIds.includes(day.id)}
                        onChange={e => setCustomDayIds(prev => e.target.checked ? [...prev, day.id] : prev.filter(id => id !== day.id))}
                        className="h-4 w-4 accent-[var(--cb-accent)]"
                        data-testid={`custom-pick-${day.day_number}`}
                      />
                    )}
                    <div className="h-9 w-9 rounded-lg flex items-center justify-center font-bold text-sm shrink-0"
                      style={{ backgroundColor: `${day.day_color || 'var(--cb-accent)'}33`, color: day.day_color || 'var(--cb-accent)' }}>
                      {day.day_number}
                    </div>
                    {editingDayId === day.id ? (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Input type="date" value={editDraft.shoot_date || ''} onChange={e => setEditDraft(p => ({ ...p, shoot_date: e.target.value }))} className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs w-32" />
                        <Input value={editDraft.call_time || ''} onChange={e => setEditDraft(p => ({ ...p, call_time: e.target.value }))} placeholder="Call" className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs w-20" />
                        <Input value={editDraft.wrap_time || ''} onChange={e => setEditDraft(p => ({ ...p, wrap_time: e.target.value }))} placeholder="Wrap" className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs w-20" />
                        <Input value={editDraft.location || ''} onChange={e => setEditDraft(p => ({ ...p, location: e.target.value }))} placeholder="Location" className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] h-7 text-xs w-40" />
                        <input type="color" value={editDraft.day_color || 'var(--cb-accent)'} onChange={e => setEditDraft(p => ({ ...p, day_color: e.target.value }))} className="h-7 w-9 rounded cursor-pointer" />
                        <label className="text-xs text-[var(--cb-text-sec)] flex items-center gap-1">
                          <input type="checkbox" checked={!!editDraft.is_day_end} onChange={e => setEditDraft(p => ({ ...p, is_day_end: e.target.checked }))} className="accent-red-500" />
                          Day End
                        </label>
                        <Button size="sm" onClick={() => { handleUpdateDay(day.id, editDraft); setEditingDayId(null); }} className="bg-[var(--cb-accent)] text-black h-7 text-xs"><Save className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingDayId(null)} className="text-[var(--cb-text-sec)] h-7 text-xs"><X className="h-3 w-3" /></Button>
                      </div>
                    ) : (
                      <div>
                        <div className="text-sm font-medium text-[var(--cb-text)]">
                          Day {day.day_number}
                          {day.is_day_end && <Badge className="ml-2 bg-red-500/20 text-red-400 border-0 text-[10px]">WRAP</Badge>}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[var(--cb-text-sec)] mt-0.5">
                          {day.shoot_date && <span className="flex items-center gap-1"><Calendar className="h-2.5 w-2.5" />{day.shoot_date}</span>}
                          {day.call_time && <span className="flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{day.call_time}{day.wrap_time ? `–${day.wrap_time}` : ''}</span>}
                          {day.location && <span className="flex items-center gap-1"><MapPin className="h-2.5 w-2.5" />{day.location}</span>}
                          <span>{(day.scene_ids || []).length} scenes</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditingDayId(day.id); setEditDraft({ shoot_date: day.shoot_date, call_time: day.call_time, wrap_time: day.wrap_time, location: day.location, day_color: day.day_color, is_day_end: day.is_day_end }); }} className="h-7 w-7 p-0 text-[var(--cb-text-sec)] hover:text-[var(--cb-accent)]"><Edit2 className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => { setShiftDialogDayId(day.id); }} className="h-7 text-xs text-[var(--cb-text-sec)] hover:text-[#4a9eff]" data-testid={`add-shift-${day.day_number}`}>
                      <Plus className="h-3 w-3 mr-0.5" /> Shift
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setCallSheetLanguageDialog({ open: true, dayId: day.id, dayNum: day.day_number })} className="h-7 text-xs text-[var(--cb-text-sec)] hover:text-[var(--cb-accent)]" data-testid={`export-callsheet-${day.day_number}`}>
                      <Download className="h-3 w-3 mr-0.5" /> Call Sheet
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteDay(day.id)} className="h-7 w-7 p-0 text-[var(--cb-text-sec)] hover:text-red-400"><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>

                {/* Location shifts */}
                {(day.location_shifts || []).length > 0 && (
                  <div className="px-4 pt-3 flex flex-wrap gap-2">
                    {day.location_shifts.map(sh => (
                      <div key={sh.id} className="rounded border px-2 py-1 text-xs flex items-center gap-2"
                        style={{ borderColor: `${sh.color}88`, backgroundColor: `${sh.color}15` }}
                        data-testid={`shift-${sh.id}`}>
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: sh.color }} />
                        <span className="text-[var(--cb-text)] font-medium">{sh.location}</span>
                        {sh.start_time && <span className="text-[var(--cb-text-sec)]">{sh.start_time}{sh.end_time ? `–${sh.end_time}` : ''}</span>}
                        <button onClick={() => handleDeleteShift(day.id, sh.id)} className="text-[var(--cb-text-sec)] hover:text-red-400">
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Scenes — 4 per row, click to expand */}
                <div className="p-4">
                  {(day.scenes || []).length === 0 ? (
                    <div className="border-2 border-dashed border-[var(--cb-border)] rounded-lg p-6 text-center text-xs text-[var(--cb-border)]">
                      Drag scenes here
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                        {(day.scenes || []).map(sc => {
                          const full = breakdownById[sc.id] || sc;
                          const isExp = expandedSceneId === sc.id;
                          return (
                            <button
                              key={sc.id}
                              onClick={() => setExpandedSceneId(isExp ? null : sc.id)}
                              draggable
                              onDragStart={e => onDragStart(e, sc.id, day.id)}
                              className={`text-left rounded border p-2.5 cursor-grab active:cursor-grabbing transition-all ${
                                isExp ? 'border-[var(--cb-accent)] bg-[var(--cb-bg)] -translate-y-0.5 shadow-lg shadow-[var(--cb-accent)]/20' : 'border-[var(--cb-border)] bg-[var(--cb-bg)]/40 hover:border-[var(--cb-accent)]/40'
                              }`}
                              data-testid={`schedule-scene-${sc.scene_number ?? 'unnumbered'}`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-bold text-[var(--cb-accent)]">{formatSceneNumber(sc.scene_number, { prefix: '#' })}</span>
                                <span className="text-[10px] text-[var(--cb-text-sec)]">{(sc.page_count_eighths || 0) / 8}pg</span>
                              </div>
                              <p className="text-xs font-medium text-[var(--cb-text)] line-clamp-2">{sc.scene_header}</p>
                              <p className="text-[10px] text-[var(--cb-text-sec)] truncate mt-0.5">{sc.location_type} · {sc.time_of_day}</p>
                            </button>
                          );
                        })}
                      </div>

                      {expandedSceneId && (day.scenes || []).some(s => s.id === expandedSceneId) && (
                        <div className="mt-3">
                          <SceneExpand
                            scene={breakdownById[expandedSceneId] || (day.scenes || []).find(s => s.id === expandedSceneId)}
                            onClose={() => setExpandedSceneId(null)}
                            onSceneChange={fetchAll}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      {/* ── Right panel: Fifi AI or Conflicts ──────────────────────────── */}
      {(showFifi || showConflicts) && (
        <div className="w-80 shrink-0 border-l border-[var(--cb-border)] flex flex-col overflow-hidden">
          {showFifi && (
            <FifiSchedulePanel
              projectId={projectId}
              token={token}
              days={days}
              breakdownById={breakdownById}
              conflicts={conflicts}
              onClose={() => setShowFifi(false)}
            />
          )}
          {showConflicts && !showFifi && (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--cb-border)] shrink-0">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-400" />
                  <span className="text-sm font-semibold text-[var(--cb-text)]">Conflicts</span>
                  {conflicts.length > 0 && (
                    <Badge className="bg-red-500/20 text-red-400 border-none text-[10px]">{conflicts.length}</Badge>
                  )}
                </div>
                <button onClick={() => setShowConflicts(false)} className="text-[var(--cb-text-dim)] hover:text-[var(--cb-text)]">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                <ConflictPanel
                  days={days}
                  breakdownById={breakdownById}
                  onJumpToDay={(dayId) => {
                    const day = days.find(d => d.id === dayId);
                    if (day) document.querySelector(`[data-testid="schedule-day-${day.day_number}"]`)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
      </div>

      {/* Add Shift Dialog */}
      <Dialog open={!!shiftDialogDayId} onOpenChange={(o) => !o && setShiftDialogDayId(null)}>
        <DialogContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)]">
          <DialogHeader>
            <DialogTitle className="font-['DM_Serif_Display'] text-xl">Add Location Shift</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Location *</label>
              <Input value={shiftDraft.location} onChange={e => setShiftDraft(p => ({ ...p, location: e.target.value }))} placeholder="e.g. Coffee Shop Interior"
                className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" data-testid="shift-location-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Start</label>
                <Input type="time" value={shiftDraft.start_time} onChange={e => setShiftDraft(p => ({ ...p, start_time: e.target.value }))}
                  className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" />
              </div>
              <div>
                <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">End</label>
                <Input type="time" value={shiftDraft.end_time} onChange={e => setShiftDraft(p => ({ ...p, end_time: e.target.value }))}
                  className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Color</label>
              <div className="flex gap-2 flex-wrap">
                {SHIFT_COLORS.map(c => (
                  <button key={c} onClick={() => setShiftDraft(p => ({ ...p, color: c }))}
                    className={`h-7 w-7 rounded-full border-2 ${shiftDraft.color === c ? 'border-white' : 'border-transparent'}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Notes</label>
              <Input value={shiftDraft.notes} onChange={e => setShiftDraft(p => ({ ...p, notes: e.target.value }))}
                placeholder="optional"
                className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShiftDialogDayId(null)} className="border-[var(--cb-border)] text-[var(--cb-text)]">Cancel</Button>
              <Button onClick={handleAddShift} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black" data-testid="shift-save-btn">Add Shift</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SceneExpand Dialog (board view) */}
      <Dialog open={!!boardExpandedSceneId} onOpenChange={o => !o && setBoardExpandedSceneId(null)}>
        <DialogContent className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] max-w-4xl w-full p-0 overflow-hidden">
          {boardExpandedSceneId && (
            <SceneExpand
              scene={breakdownById[boardExpandedSceneId] || allScenes.find(s => s.id === boardExpandedSceneId)}
              onClose={() => setBoardExpandedSceneId(null)}
              onSceneChange={fetchAll}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Auto-Schedule Dialog */}
      <Dialog open={autoScheduleDialog} onOpenChange={setAutoScheduleDialog}>
        <DialogContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['DM_Serif_Display'] text-2xl flex items-center gap-2">
              <Zap className="h-5 w-5 text-[var(--cb-accent)]" /> AI Auto-Schedule
            </DialogTitle>
            <DialogDescription className="text-[var(--cb-text-sec)]">
              Intelligent scheduling with FEFKA/Kerala industry constraints. Only unscheduled scenes are touched.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Pages per day */}
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Pages per day (FEFKA norm: 8–10)</label>
              <div className="flex items-center gap-3">
                <input
                  type="range" min="4" max="12" step="0.5"
                  value={autoScheduleOptions.pages_per_day}
                  onChange={e => setAutoScheduleOptions(o => ({ ...o, pages_per_day: parseFloat(e.target.value) }))}
                  className="flex-1 accent-[var(--cb-accent)]"
                />
                <span className="text-sm text-[var(--cb-accent)] w-12 text-right">{autoScheduleOptions.pages_per_day}p</span>
              </div>
            </div>
            {/* Options */}
            <div className="space-y-2">
              {[
                { key: 'prioritize_location_grouping', label: 'Group scenes by location (minimise company moves)' },
                { key: 'shoot_vfx_early', label: 'Schedule VFX/stunt scenes in first third of shoot' },
                { key: 'respect_story_order', label: 'Respect story order (emotional scenes after Week 1)' },
                { key: 'monsoon_mode', label: '🌧 Monsoon mode (Jun–Oct Kerala) — prioritise INT scenes, add weather cover warnings' },
              ].map(opt => (
                <label key={opt.key} className="flex items-start gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={autoScheduleOptions[opt.key]}
                    onChange={e => setAutoScheduleOptions(o => ({ ...o, [opt.key]: e.target.checked }))}
                    className="mt-0.5 accent-[var(--cb-accent)]"
                  />
                  <span className="text-xs text-[var(--cb-text-sec)] group-hover:text-[var(--cb-text)] transition-colors">{opt.label}</span>
                </label>
              ))}
            </div>
            <div className="bg-[var(--cb-bg)] border border-[var(--cb-border)] rounded-lg p-3 text-xs text-[var(--cb-text-dim)] space-y-1">
              <p>✓ Max {autoScheduleOptions.pages_per_day}p/day (FEFKA union rule)</p>
              <p>✓ Golden hour scenes (dawn/dusk) capped at 2 per day</p>
              <p>✓ Flagged scenes (stunt/VFX/pyro) scheduled first</p>
              <p>✓ Actors grouped to minimise holding days</p>
              {locSpecs.length > 0 && <p>✓ {locSpecs.length} location spec(s) applied for distance sorting</p>}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setAutoScheduleDialog(false); setViewMode('locations'); if (!locGallery.length) fetchLocGallery(); }}
                className="border-[var(--cb-border)] text-[var(--cb-text-sec)] gap-1" data-testid="open-loc-specs-btn">
                <MapPin className="h-4 w-4" /> Location Specs
              </Button>
              <Button variant="outline" onClick={() => setAutoScheduleDialog(false)} className="border-[var(--cb-border)] text-[var(--cb-text)]">Cancel</Button>
              <Button onClick={handleAutoGenerate} disabled={generating} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black gap-1" data-testid="confirm-auto-schedule-btn">
                <Zap className="h-4 w-4" /> {generating ? 'Scheduling…' : 'Schedule Now'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Global Lightbox */}
      {locLightbox && (
        <ImageLightbox
          images={locLightbox.images}
          initialIndex={locLightbox.idx}
          onClose={() => setLocLightbox(null)}
        />
      )}

      <PdfLanguageDialog
        open={callSheetLanguageDialog.open}
        onClose={() => setCallSheetLanguageDialog({ open: false, dayId: null, dayNum: null })}
        onConfirm={handleExportCallSheetWithLanguage}
        title="Choose call sheet PDF synopsis language"
      />

      {/* D&D Location Availability Warning Dialog */}
      <Dialog open={!!dndWarning} onOpenChange={o => !o && setDndWarning(null)}>
        <DialogContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="h-5 w-5" /> Location Availability Warning
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[var(--cb-text)] mt-2">{dndWarning?.message}</p>
          <p className="text-xs text-[var(--cb-text-sec)] mt-1">You can still schedule this scene on this day.</p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDndWarning(null)} className="border-[var(--cb-border)] text-[var(--cb-text)]" data-testid="dnd-warning-cancel">Cancel</Button>
            <Button onClick={handleDndWarningConfirm} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="dnd-warning-confirm">Ignore &amp; Add Anyway</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchedulePage;
